import { useEffect } from "react";
import { useAppSelector } from "../../store/hooks";
import { Helmet } from "react-helmet";
import React from "react";
import {
  findActiveLanguageByCode,
  languageCodesEqual,
  languageCodeFromActiveLanguage,
  normalizeLanguageCode,
} from "./utilities/languageCode";
import { getPageLocaleUrl } from "./utilities/helpers";
import usePageLanguage from "./hooks/usePageLanguage";

/** BCP 47 for <html lang>: prefer language settings `locale`, else webpage code (`et_EE` → `et-EE`). */
function resolveHtmlLang(
  siteLanguageCode: string,
  activeLanguages: Array<{ locale?: string }>
): string {
  const row = findActiveLanguageByCode(activeLanguages, siteLanguageCode);
  const loc =
    typeof row?.locale === "string" && row.locale.trim() !== ""
      ? row.locale.trim().replace(/_/g, "-")
      : "";
  if (loc) return loc;
  return siteLanguageCode.trim().replace(/_/g, "-") || "et";
}

export default function MetadataComponent({ page_seo }) {
  const base_font_size = useAppSelector(
    (state) => state.builder.website.baseFontSize ?? "10px"
  );
  const activePageUuid = useAppSelector((state) => state.builder.pages.active);
  const pages = useAppSelector((state) => state.builder.pages.list) as any[];
  const languageSettings = useAppSelector((state) => state.builder.languageSettings);
  const activeLanguages = Array.isArray(languageSettings?.activeLanguages)
    ? languageSettings.activeLanguages
    : [];
  const pageLanguageFromStore = usePageLanguage();

  const matchPathTemplate = (template: string, pathname: string) => {
    const templateParts = template.split("/").filter(Boolean);
    const pathParts = pathname.split("/").filter(Boolean);
    if (templateParts.length !== pathParts.length) return null;
    const values: string[] = [];
    for (let i = 0; i < templateParts.length; i += 1) {
      const t = templateParts[i];
      const p = pathParts[i];
      if (t.startsWith(":")) {
        values.push(p);
      } else if (normalizeLanguageCode(t) !== normalizeLanguageCode(p)) {
        return null;
      }
    }
    return values;
  };

  const fillPathTemplateParams = (template: string, values: string[]) => {
    let index = 0;
    return template.replace(/:[^/]+/g, () => {
      const next = values[index] ?? "";
      index += 1;
      return next;
    });
  };

  const currentPathname = typeof window !== "undefined" ? window.location.pathname : "";

  const matchedPages = pages
    .map((page) => {
      const template = getPageLocaleUrl(page, activeLanguages);
      const params = matchPathTemplate(template, currentPathname);
      if (!params) return null;
      return { page, template, params };
    })
    .filter((row) => row != null) as Array<{ page: any; template: string; params: string[] }>;

  const matchedCurrentByPath =
    matchedPages.sort((a, b) => b.template.length - a.template.length)[0] ?? null;

  const getFallbackAlternates = (languageCodes) => {
    if (typeof window === "undefined") {
      return null;
    }

    const origin = window.location.origin;
    const pathnameParts = window.location.pathname.split("/").filter(Boolean);
    const normalizedCodes = languageCodes
      .map((code) => String(code ?? "").trim())
      .filter((code) => code !== "");
    const hasLocalePrefix = normalizedCodes.some(
      (code) => normalizeLanguageCode(code) === normalizeLanguageCode(pathnameParts[0])
    );
    const pathWithoutLocaleParts = hasLocalePrefix
      ? pathnameParts.slice(1).join("/")
      : pathnameParts.join("/");
    const pathWithoutLocale = pathWithoutLocaleParts
      ? `/${pathWithoutLocaleParts}`
      : "";
    const links = {};

    normalizedCodes.forEach((code) => {
      links[code] = `${origin}/${code}${pathWithoutLocale}`;
    });

    return links;
  };

  const currentPage = pages?.find((p) => p.uuid === activePageUuid);
  const resolvedCurrentPage = matchedCurrentByPath?.page ?? currentPage;
  const currentParams = matchedCurrentByPath?.params ?? [];
  const translatedPages: any[] = [];
  const seenPageKeys = new Set<string>();
  const translationLanguageCodes = new Set<string>();

  const addTranslatedPage = (page: any) => {
    if (!page) return;
    const key = String(page.uuid ?? "").trim() || String(page.id ?? "");
    if (!key || seenPageKeys.has(key)) return;
    seenPageKeys.add(key);
    translatedPages.push(page);
  };

  if (resolvedCurrentPage) {
    addTranslatedPage(resolvedCurrentPage);
    if (
      typeof resolvedCurrentPage.languageCode === "string" &&
      resolvedCurrentPage.languageCode.trim() !== ""
    ) {
      translationLanguageCodes.add(resolvedCurrentPage.languageCode.trim());
    }

    if (resolvedCurrentPage.languageGroup != null) {
      pages?.forEach((page: any) => {
        if (page.languageGroup === resolvedCurrentPage.languageGroup) {
          addTranslatedPage(page);
        }
      });
    }

    if (Array.isArray(resolvedCurrentPage.translationWebpages)) {
      resolvedCurrentPage.translationWebpages.forEach((translation) => {
        if (typeof translation.languageCode === "string" && translation.languageCode.trim() !== "") {
          translationLanguageCodes.add(translation.languageCode.trim());
        }
        const translatedPage = pages?.find((p) => {
          if (translation.target_webpage_uuid && p.uuid === translation.target_webpage_uuid) {
            return true;
          }
          if (translation.target_webpage_id != null && p.id === translation.target_webpage_id) {
            return true;
          }
          return false;
        });
        addTranslatedPage(translatedPage);
      });
    }

    pages?.forEach((page: any) => {
      if (page.id === resolvedCurrentPage.id || !Array.isArray(page.translationWebpages)) {
        return;
      }
      const linksToCurrent = page.translationWebpages.some((translation) => {
        if (
          translation.target_webpage_uuid &&
          translation.target_webpage_uuid === resolvedCurrentPage.uuid
        ) {
          return true;
        }
        if (
          translation.target_webpage_id != null &&
          translation.target_webpage_id === resolvedCurrentPage.id
        ) {
          return true;
        }
        return false;
      });
      if (linksToCurrent) {
        addTranslatedPage(page);
      }
    });

    if (translatedPages.length <= 1) {
      pages?.forEach((page: any) => {
        const sameType = page.pageType === resolvedCurrentPage.pageType;
        const sameUrl = normalizeLanguageCode(page.url) === normalizeLanguageCode(resolvedCurrentPage.url);
        const bothHome = Boolean(page.homepage) && Boolean(resolvedCurrentPage.homepage);
        if (sameType && (sameUrl || bothHome)) {
          addTranslatedPage(page);
        }
      });
    }
  }

  const availableLanguageCodes = translatedPages
    .map((page) => {
      const language = findActiveLanguageByCode(activeLanguages, page.languageCode);
      return languageCodeFromActiveLanguage(language);
    })
    .filter((code) => typeof code === "string" && code.trim() !== "");
  const availableCodeSet = new Set(
    availableLanguageCodes.map((code) => normalizeLanguageCode(code)).filter(Boolean)
  );

  activeLanguages.forEach((lang) => {
    const code = languageCodeFromActiveLanguage(lang);
    if (!code) return;
    const matchesTranslation = Array.from(translationLanguageCodes).some((translationCode) =>
      languageCodesEqual(translationCode, code)
    );
    if (!matchesTranslation) return;
    if (!availableCodeSet.has(normalizeLanguageCode(code))) {
      availableLanguageCodes.push(code);
      availableCodeSet.add(normalizeLanguageCode(code));
    }
  });

  const fallbackAlternates = getFallbackAlternates(availableLanguageCodes);
  const seoAlternates = page_seo?.alternates ?? {};
  const alternateLinks = { ...(fallbackAlternates ?? {}) };

  translatedPages.forEach((page) => {
    const lang = findActiveLanguageByCode(activeLanguages, page.languageCode);
    const code = languageCodeFromActiveLanguage(lang);
    if (!code) return;
    const template = getPageLocaleUrl(page, activeLanguages);
    const filled = fillPathTemplateParams(template, currentParams);
    if (filled) {
      alternateLinks[code] = `${window.location.origin}${filled}`;
    }
  });

  availableLanguageCodes.forEach((code) => {
    if (seoAlternates[code]) {
      alternateLinks[code] = seoAlternates[code];
    }
  });

  // x-default: same URL as the page the visitor is on (same locale segment as current URL).
  if (typeof window !== "undefined") {
    alternateLinks["x-default"] = `${window.location.origin}${window.location.pathname}`;
  }

  const siteLanguageCode =
    resolvedCurrentPage &&
    typeof resolvedCurrentPage.languageCode === "string" &&
    resolvedCurrentPage.languageCode.trim() !== ""
      ? resolvedCurrentPage.languageCode.trim()
      : pageLanguageFromStore;
  const htmlLang = resolveHtmlLang(siteLanguageCode, activeLanguages);

  useEffect(() => {
    document.body.style.fontSize = base_font_size;
    document.getElementsByTagName("html")[0].style.fontSize = base_font_size;
  });

  return (
    <Helmet htmlAttributes={{ lang: htmlLang }}>
      <meta charSet="utf-8" />

      <meta http-equiv="cache-control" content="no-cache" />
      <meta http-equiv="expires" content="0" />
      <meta http-equiv="pragma" content="no-cache" />

      {page_seo.seo_title && <title>{page_seo.seo_title}</title>}
      {page_seo.seo_description && (
        <meta name="description" content={page_seo.seo_description} />
      )}
      {page_seo.canonical && <link rel="canonical" href={page_seo.canonical} />}
      {availableLanguageCodes.map((code) =>
        alternateLinks[code] ? (
          <link key={code} rel="alternate" hrefLang={code} href={alternateLinks[code]} />
        ) : null
      )}
      {alternateLinks["x-default"] && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={alternateLinks["x-default"]}
        />
      )}
      <meta
        name="robots"
        content={page_seo.seo_no_index ? "noindex, nofollow" : "index, follow"}
      />
      {page_seo.seo_keyword && (
        <meta name="keywords" content={page_seo.seo_keyword} />
      )}

      {/* https://developers.facebook.com/docs/sharing/webmasters/ */}
      {page_seo.seo_og_title && (
        <meta name="og:title" content={page_seo.seo_og_title} />
      )}
      {page_seo.seo_og_description && (
        <meta name="og:description" content={page_seo.seo_og_description} />
      )}
      {page_seo.seo_og_url && (
        <meta name="og:url" content={page_seo.seo_og_url} />
      )}
      {page_seo.seo_og_type && (
        <meta name="og:type" content={page_seo.seo_og_type} />
      )}
      {page_seo.seo_og_image_url && (
        <meta name="og:image" content={page_seo.seo_og_image_url} />
      )}

      {/* https://developer.twitter.com/en/docs/twitter-for-websites/cards/overview/markup */}
      {page_seo.seo_twitter_title && (
        <meta name="twitter:title" content={page_seo.seo_twitter_title} />
      )}
      {page_seo.seo_twitter_description && (
        <meta
          name="twitter:description"
          content={page_seo.seo_twitter_description}
        />
      )}
      {page_seo.seo_twitter_image && (
        <meta name="twitter:image" content={page_seo.seo_twitter_image} />
      )}
      {page_seo.seo_twitter_card && (
        <meta name="twitter:card" content={page_seo.seo_twitter_card} />
      )}
    </Helmet>
  );
}
